import React, { useState, useEffect, useCallback } from 'react';
import cashflowService from '../services/cashflowService';
import clientHelperService from '../services/clientHelperService';
import sharepointService from '../services/sharepointService';
import ClientFormView from '../components/ClientFormView';
import CashflowSummaryView from '../components/CashflowSummaryView';
import CashflowSharePointModal from '../components/CashflowSharePointModal';
import ClientFormScheduleModal from '../components/ClientFormScheduleModal';

// The meeting the Friendly Reminder email says is waiting on the worksheet.
// Clients without a choice are treated as 'alignment' by the backend.
const NEXT_MEETING_OPTIONS = [
  { key: 'alignment', label: 'New client – Alignment (Matt/Bradford)', short: 'Alignment', badgeClass: 'bg-secondary-subtle text-secondary-emphasis' },
  { key: 'fp_getting_started', label: 'Existing client – Getting Started with FP (Lisa)', short: 'Getting Started', badgeClass: 'bg-info-subtle text-info-emphasis' },
  { key: 'discovery', label: 'Existing client – Discovery', short: 'Discovery', badgeClass: 'bg-primary-subtle text-primary-emphasis' },
];

const nextMeetingOption = (key) => NEXT_MEETING_OPTIONS.find((o) => o.key === key) || NEXT_MEETING_OPTIONS[0];

// Fixed positioning lets row menus escape the .table-responsive overflow clip.
const FIXED_POPPER = JSON.stringify({ strategy: 'fixed' });

const EMPTY_FORM = { name: '', email: '', phone: '', notes: '', sharepoint_folder: '', newSpFolderName: '', knowledgebase_client_id: '', newKbClientName: '', client_type: 'hff', next_meeting: 'alignment', send_mode: 'now', scheduled_send_local: '' };

const CashflowAdmin = ({ embedded = false } = {}) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Publish to SharePoint modal
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishClient, setPublishClient] = useState(null);

  // Knowledgebase clients list (for create form dropdown)
  const [kbClients, setKbClients] = useState([]);

  // SharePoint client folders list (for create form dropdown)
  const [spClients, setSpClients] = useState([]);

  // Link copy feedback
  const [copiedId, setCopiedId] = useState(null);

  // Get Link modal
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [magicLink, setMagicLink] = useState('');
  const [linkClient, setLinkClient] = useState(null);

  // Schedule modal
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleClient, setScheduleClient] = useState(null);

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Status filter — 'all' | 'completed' | 'in_progress' | 'not_started'
  const [statusFilter, setStatusFilter] = useState('all');
  const [showProcessed, setShowProcessed] = useState(false);

  // Sort
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function sortIcon(key) {
    if (sortKey !== key) return <i className="bi bi-chevron-expand text-muted ms-1" style={{ fontSize: '0.7rem' }}></i>;
    return sortDir === 'asc'
      ? <i className="bi bi-chevron-up ms-1" style={{ fontSize: '0.7rem' }}></i>
      : <i className="bi bi-chevron-down ms-1" style={{ fontSize: '0.7rem' }}></i>;
  }

  // Client detail view
  const [detailClient, setDetailClient] = useState(null);
  const [detailForm, setDetailForm] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState('summary');

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await cashflowService.fetchAllClients();
      setClients(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  // Load knowledgebase clients and SharePoint client folders for the create-form dropdowns.
  useEffect(() => {
    clientHelperService.fetchClients().then(setKbClients).catch(() => setKbClients([]));
    sharepointService.getClients().then(data => setSpClients(data)).catch(() => setSpClients([]));
  }, []);

  // Find a knowledgebase client that matches a SharePoint folder name.
  const findMatchingKbClient = (spFolderName) => {
    if (!spFolderName || !kbClients.length) return null;
    const normalized = spFolderName.replace(/_REBELLIONAIRE/gi, '').toLowerCase().trim();
    let match = kbClients.find(c => c.client_name.toLowerCase().trim() === normalized);
    if (match) return match;
    match = kbClients.find(c =>
      c.client_name.toLowerCase().includes(normalized) ||
      normalized.includes(c.client_name.toLowerCase())
    );
    if (match) return match;
    match = kbClients.find(c =>
      c.nicknames && c.nicknames.some(n =>
        n.toLowerCase().trim() === normalized ||
        n.toLowerCase().includes(normalized) ||
        normalized.includes(n.toLowerCase())
      )
    );
    return match;
  };

  // Handle SharePoint folder selection — try to auto-preselect the matching KB client.
  const handleSpFolderChange = (value) => {
    const update = { sharepoint_folder: value, newSpFolderName: '', knowledgebase_client_id: '', newKbClientName: '' };
    if (value && value !== '__new__') {
      const match = findMatchingKbClient(value);
      if (match) update.knowledgebase_client_id = match.client_id;
    }
    setFormData(prev => ({ ...prev, ...update }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError('');

      let kbClientId = formData.knowledgebase_client_id;

      // Handle new SharePoint folder creation.
      if (formData.sharepoint_folder === '__new__') {
        const spName = formData.newSpFolderName.trim();
        if (!spName) {
          setError('Please enter a name for the new SharePoint folder.');
          setSubmitting(false);
          return;
        }
        try {
          await sharepointService.createClient(spName);
        } catch (spErr) {
          console.warn('SharePoint folder creation failed (may already exist):', spErr);
        }
        sharepointService.getClients().then(data => setSpClients(data)).catch(() => { });
      }

      // Handle new KB client creation.
      if (formData.knowledgebase_client_id === '__new__') {
        const kbName = formData.newKbClientName.trim();
        if (!kbName) {
          setError('Please enter a name for the new knowledgebase client.');
          setSubmitting(false);
          return;
        }
        const kbResult = await clientHelperService.createKbClient({ client_name: kbName });
        kbClientId = kbResult.client_id;
        clientHelperService.fetchClients().then(setKbClients).catch(() => { });
      }

      const { newKbClientName, sharepoint_folder, newSpFolderName, scheduled_send_local, ...clientPayload } = formData;
      const scheduledSendAt = scheduled_send_local ? new Date(scheduled_send_local).toISOString() : null;
      const result = await cashflowService.createClient({
        ...clientPayload,
        knowledgebase_client_id: kbClientId || '',
        scheduled_send_at: scheduledSendAt,
      });
      if (result.delivery_status === 'scheduled') {
        setSuccess(`Client created. Initial send scheduled for ${new Date(result.scheduled_send_at).toLocaleString()}.`);
      } else {
        setSuccess(`Client created. Magic link: ${result.magic_link}`);
      }
      setShowCreateModal(false);
      setFormData(EMPTY_FORM);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedClient) return;
    try {
      setSubmitting(true);
      setError('');
      await cashflowService.deleteClient(selectedClient.client_id);
      setSuccess(`Deleted client: ${selectedClient.name}`);
      setShowDeleteModal(false);
      setSelectedClient(null);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (client) => {
    try {
      setError('');
      const result = await cashflowService.resendLink(client.client_id);
      setSuccess(`Magic link resent to ${client.name}: ${result.magic_link}`);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleToggleProcessed = async (client) => {
    try {
      setError('');
      const nextProcessed = !client.processed;
      await cashflowService.patchClient(client.client_id, { processed: nextProcessed });
      setSuccess(`${nextProcessed ? 'Marked' : 'Unmarked'} as processed: ${client.name}`);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleTogglePause = async (client) => {
    try {
      setError('');
      const pausing = !client.reminders_paused;
      await cashflowService.patchClient(client.client_id, { reminders_paused: pausing });
      setSuccess(`Reminders ${pausing ? 'paused' : 'resumed'} for ${client.name}.`);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleChangeNextMeeting = async (client, nextMeeting) => {
    try {
      setError('');
      await cashflowService.patchClient(client.client_id, { next_meeting: nextMeeting });
      const label = NEXT_MEETING_OPTIONS.find((o) => o.key === nextMeeting)?.label || nextMeeting;
      setSuccess(`Reminder emails for ${client.name} now mention: ${label}.`);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleGetLink = async (client) => {
    setLinkClient(client);
    setMagicLink('');
    setShowLinkModal(true);
    try {
      setLinkLoading(true);
      setError('');
      const result = await cashflowService.getLink(client.client_id);
      setMagicLink(result.magic_link);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setShowLinkModal(false);
    } finally {
      setLinkLoading(false);
    }
  };

  const handleOpenScheduleModal = (client) => {
    setScheduleClient(client);
    setShowScheduleModal(true);
  };

  const handleScheduleSave = async (payload) => {
    if (!scheduleClient) return;
    try {
      setSubmitting(true);
      setError('');
      await cashflowService.scheduleSend(scheduleClient.client_id, payload);
      setSuccess(`Scheduled send saved for ${scheduleClient.name}.`);
      setShowScheduleModal(false);
      setScheduleClient(null);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleScheduleCancel = async () => {
    if (!scheduleClient) return;
    try {
      setSubmitting(true);
      setError('');
      await cashflowService.cancelScheduledSend(scheduleClient.client_id);
      setSuccess(`Canceled scheduled send for ${scheduleClient.name}.`);
      setShowScheduleModal(false);
      setScheduleClient(null);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text, clientId) => {
    navigator.clipboard.writeText(text);
    setCopiedId(clientId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleViewClient = async (client) => {
    if (detailClient?.client_id === client.client_id) {
      setDetailClient(null);
      setDetailForm(null);
      return;
    }
    try {
      setDetailLoading(true);
      setDetailClient(client);
      setDetailForm(null);
      const data = await cashflowService.getClientDetail(client.client_id);
      setDetailForm(data.form?.form_data || {});
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setDetailClient(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleExport = async (client) => {
    try {
      setError('');
      const response = await cashflowService.exportClientExcel(client.client_id);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${client.name.replace(/ /g, '_')}_HFFAnnualCashflow.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Export failed');
    }
  };

  const handlePublish = (client) => {
    setPublishClient(client);
    setShowPublishModal(true);
  };

  const handlePublishSuccess = (result) => {
    setShowPublishModal(false);
    setPublishClient(null);
    setSuccess(
      `Published to SharePoint: ${result.sharepoint_location || 'success'}` +
      (result.knowledgebase_status === 'added_to_knowledgebase'
        ? ' — added to knowledgebase.'
        : '')
    );
  };

  const filteredClients = clients.filter(c => {
    const matchesSearch =
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    if (!showProcessed && c.processed) return false;
    const isComplete = !!c.completed || (c.total_sections > 0 && c.sections_completed === c.total_sections);
    if (statusFilter === 'completed') return isComplete;
    if (statusFilter === 'in_progress') return !isComplete && (c.sections_completed || 0) > 0;
    if (statusFilter === 'not_started') return !isComplete && (c.sections_completed || 0) === 0;
    return true;
  });

  const sortedClients = sortKey
    ? [...filteredClients].sort((a, b) => {
      let av, bv;
      if (sortKey === 'status') {
        const aProcessed = !!a.processed;
        const bProcessed = !!b.processed;
        const aComplete = a.completed || (a.total_sections > 0 && a.sections_completed === a.total_sections);
        const bComplete = b.completed || (b.total_sections > 0 && b.sections_completed === b.total_sections);
        av = aProcessed ? 102 : (aComplete ? 101 : (a.sections_completed || 0));
        bv = bProcessed ? 102 : (bComplete ? 101 : (b.sections_completed || 0));
      } else if (sortKey === 'meeting') {
        av = NEXT_MEETING_OPTIONS.indexOf(nextMeetingOption(a.next_meeting));
        bv = NEXT_MEETING_OPTIONS.indexOf(nextMeetingOption(b.next_meeting));
      } else if (sortKey === 'created') {
        av = a.created_at || '';
        bv = b.created_at || '';
      } else {
        av = (a[sortKey] || '').toLowerCase();
        bv = (b[sortKey] || '').toLowerCase();
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    })
    : filteredClients;

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const Wrapper = embedded ? 'div' : 'div';
  const wrapperClass = embedded ? '' : 'container-fluid py-4';

  return (
    <Wrapper className={wrapperClass}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        {embedded ? (
          <div className="text-muted small">
            <i className="bi bi-info-circle me-1"></i>
            Send the Annual Cashflow Wizard to clients for a full income/expense intake.
          </div>
        ) : (
          <h2 className="mb-0">
            <i className="bi bi-cash-stack me-2"></i>
            Cashflow Wizard — Client Management
          </h2>
        )}
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <i className="bi bi-plus-lg me-1"></i> New Client
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}
      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <div style={{ wordBreak: 'break-all' }}>{success}</div>
          {success.includes('http') && (
            <button
              className="btn btn-sm btn-outline-success mt-2"
              onClick={() => {
                const url = success.match(/(https?:\/\/\S+)/)?.[1];
                if (url) copyToClipboard(url, 'success');
              }}
            >
              <i className="bi bi-clipboard me-1"></i>
              {copiedId === 'success' ? 'Copied!' : 'Copy Link'}
            </button>
          )}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      <div className="mb-3 d-flex gap-2 flex-wrap align-items-center">
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: '320px' }}
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="btn-group">
          {[
            { key: 'all', label: 'All' },
            { key: 'completed', label: 'Completed' },
            { key: 'in_progress', label: 'In Progress' },
            { key: 'not_started', label: 'Not Started' },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`btn btn-sm ${statusFilter === key ? 'btn-primary' : 'btn-outline-secondary'
                }`}
              onClick={() => setStatusFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={`btn btn-sm ${showProcessed ? 'btn-warning' : 'btn-outline-secondary'}`}
          onClick={() => setShowProcessed((v) => !v)}
          title={showProcessed ? 'Showing processed workflows' : 'Hiding processed workflows'}
        >
          <i className={`bi ${showProcessed ? 'bi-eye' : 'bi-eye-slash'} me-1`} />
          {showProcessed ? 'Hide Processed' : 'Show Processed'}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-inbox display-4 d-block mb-2"></i>
          {searchTerm || statusFilter !== 'all' || !showProcessed ? 'No clients match your filters.' : 'No cashflow clients yet. Click "New Client" to create one.'}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>Client{sortIcon('name')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>Status{sortIcon('status')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('meeting')}>Meeting{sortIcon('meeting')}</th>
                <th>Reminders</th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('created')}>Created{sortIcon('created')}</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedClients.map(client => {
                const filled = client.sections_completed || 0;
                const totalSections = client.total_sections || 14;
                const isComplete = client.completed || (totalSections > 0 && filled === totalSections);
                const meeting = nextMeetingOption(client.next_meeting);
                return (
                  <tr
                    key={client.client_id}
                    onClick={() => handleViewClient(client)}
                    style={{ cursor: 'pointer' }}
                    className={detailClient?.client_id === client.client_id ? 'table-active' : ''}
                  >
                    <td>
                      <div className="fw-semibold">
                        {client.name}
                        {client.device_type && (
                          <i
                            className={`bi ${client.device_type === 'mobile' ? 'bi-phone' : 'bi-laptop'} text-muted ms-2`}
                            title={`Filling in on ${client.device_type === 'mobile' ? 'mobile' : 'desktop'}`}
                          ></i>
                        )}
                      </div>
                      <div className="small text-muted">{client.email}</div>
                      {client.phone && <div className="small text-muted">{client.phone}</div>}
                    </td>
                    <td style={{ minWidth: '130px' }}>
                      {(() => {
                        const pct = isComplete ? 100 : Math.round((filled / totalSections) * 100);
                        const color = isComplete ? 'bg-success' : pct > 0 ? 'bg-info' : 'bg-secondary';
                        return (
                          <div>
                            <div className="progress" style={{ height: '18px' }}>
                              <div
                                className={`progress-bar ${color}`}
                                role="progressbar"
                                style={{ width: `${Math.max(pct, pct > 0 ? 10 : 0)}%` }}
                              >
                                {pct > 0 ? `${pct}%` : ''}
                              </div>
                            </div>
                            <small className="text-muted">
                              {client.processed
                                ? 'Processed'
                                : (isComplete ? 'Completed' : pct === 0 ? 'Not Started' : `${filled} of ${totalSections} sections`)}
                            </small>
                          </div>
                        );
                      })()}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="dropdown">
                        <button
                          type="button"
                          className={`badge border-0 dropdown-toggle ${meeting.badgeClass}`}
                          data-bs-toggle="dropdown"
                          data-bs-popper-config={FIXED_POPPER}
                          aria-expanded="false"
                          title="Meeting named in the reminder email. Click to change."
                        >
                          {meeting.short}
                        </button>
                        <ul className="dropdown-menu">
                          <li><h6 className="dropdown-header">Reminder email mentions</h6></li>
                          {NEXT_MEETING_OPTIONS.map((opt) => (
                            <li key={opt.key}>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => opt.key !== meeting.key && handleChangeNextMeeting(client, opt.key)}
                              >
                                <i className={`bi bi-check2 me-2 ${opt.key === meeting.key ? '' : 'invisible'}`}></i>
                                {opt.label}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </td>
                    <td>
                      {(() => {
                        if (client.processed || isComplete) {
                          return <span className="text-muted">—</span>;
                        }
                        if (client.reminders_paused) {
                          return (
                            <span className="badge bg-warning text-dark">
                              <i className="bi bi-pause-circle me-1" />Paused
                            </span>
                          );
                        }
                        const ref = client.last_reminder_sent || client.created_at;
                        if (!ref) return <span className="text-muted">—</span>;
                        const daysSince = Math.floor((Date.now() - new Date(ref).getTime()) / 86400000);
                        const daysLeft = 10 - daysSince;
                        if (daysLeft <= 0) {
                          return (
                            <span className="badge bg-danger">
                              <i className="bi bi-envelope me-1" />Due now
                            </span>
                          );
                        }
                        return <span className="text-muted small">{daysLeft}d until next</span>;
                      })()}
                      {client.next_scheduled_send_at && (
                        <div
                          className="text-muted text-nowrap"
                          style={{ fontSize: '0.75rem' }}
                          title={`Scheduled send: ${client.next_scheduled_send_reason || 'scheduled'}`}
                        >
                          <i className="bi bi-calendar-event me-1"></i>
                          Send {formatDate(client.next_scheduled_send_at)}
                        </div>
                      )}
                    </td>
                    <td className="text-nowrap">
                      <div className="small">{formatDate(client.created_at)}</div>
                      <div className="text-muted" style={{ fontSize: '0.75rem' }} title="Magic link expiry">
                        Link expires {formatDate(client.token_expires_at)}
                      </div>
                    </td>
                    <td className="text-end" onClick={(e) => e.stopPropagation()}>
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-secondary"
                          title="Get magic link"
                          onClick={() => handleGetLink(client)}
                        >
                          <i className="bi bi-link-45deg"></i>
                        </button>
                        <button
                          className="btn btn-outline-primary"
                          title="Resend magic link via email"
                          onClick={() => handleResend(client)}
                        >
                          <i className="bi bi-send"></i>
                        </button>
                        <div className="btn-group btn-group-sm">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            data-bs-toggle="dropdown"
                            data-bs-popper-config={FIXED_POPPER}
                            aria-expanded="false"
                            title="More actions"
                          >
                            <i className="bi bi-three-dots"></i>
                          </button>
                          <ul className="dropdown-menu dropdown-menu-end">
                            <li>
                              <button type="button" className="dropdown-item" onClick={() => handleTogglePause(client)}>
                                <i className={`bi ${client.reminders_paused ? 'bi-play-circle' : 'bi-pause-circle'} me-2`}></i>
                                {client.reminders_paused ? 'Resume reminder emails' : 'Pause reminder emails'}
                              </button>
                            </li>
                            <li>
                              <button type="button" className="dropdown-item" onClick={() => handleOpenScheduleModal(client)}>
                                <i className="bi bi-calendar-plus me-2"></i>Schedule next form send
                              </button>
                            </li>
                            <li>
                              <button type="button" className="dropdown-item" onClick={() => handleExport(client)}>
                                <i className="bi bi-file-earmark-spreadsheet me-2"></i>Export to Excel
                              </button>
                            </li>
                            <li>
                              <button type="button" className="dropdown-item" onClick={() => handlePublish(client)}>
                                <i className="bi bi-cloud-upload me-2"></i>Publish to SharePoint
                              </button>
                            </li>
                            <li>
                              <button type="button" className="dropdown-item" onClick={() => handleToggleProcessed(client)}>
                                <i className={`bi ${client.processed ? 'bi-arrow-counterclockwise' : 'bi-check2-circle'} me-2`}></i>
                                {client.processed ? 'Mark as unprocessed' : 'Mark as processed'}
                              </button>
                            </li>
                            <li><hr className="dropdown-divider" /></li>
                            <li>
                              <button
                                type="button"
                                className="dropdown-item text-danger"
                                onClick={() => { setSelectedClient(client); setShowDeleteModal(true); }}
                              >
                                <i className="bi bi-trash me-2"></i>Delete client
                              </button>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Client Detail Panel */}
      {detailClient && (
        <div className="card mt-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-person-lines-fill me-2"></i>
              {detailClient.name}
              <small className="text-muted ms-2">({detailClient.email})</small>
            </h5>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => { setDetailClient(null); setDetailForm(null); }}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
          <div className="card-body">
            {detailLoading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                <ul className="nav nav-tabs mb-3">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${detailTab === 'summary' ? 'active' : ''}`}
                      onClick={() => setDetailTab('summary')}
                    >
                      <i className="bi bi-calculator me-1"></i> Summary
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${detailTab === 'form' ? 'active' : ''}`}
                      onClick={() => setDetailTab('form')}
                    >
                      <i className="bi bi-list-columns me-1"></i> Form Data
                    </button>
                  </li>
                </ul>

                {detailTab === 'summary' ? (
                  <CashflowSummaryView formData={detailForm} />
                ) : (
                  <ClientFormView formData={detailForm} />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Create Client Modal */}
      {showCreateModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create Cashflow Client</h5>
                <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)}></button>
              </div>
              <form onSubmit={handleCreate}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email <span className="text-danger">*</span></label>
                    <input
                      type="email"
                      className="form-control"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Phone</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="+1-555-555-0123"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Send Timing</label>
                    <div className="d-flex gap-3 mb-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="send_mode"
                          id="cashflow-send-now"
                          value="now"
                          checked={formData.send_mode === 'now'}
                          onChange={(e) => setFormData({ ...formData, send_mode: e.target.value })}
                        />
                        <label className="form-check-label" htmlFor="cashflow-send-now">
                          Send now
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="send_mode"
                          id="cashflow-send-scheduled"
                          value="scheduled"
                          checked={formData.send_mode === 'scheduled'}
                          onChange={(e) => setFormData({ ...formData, send_mode: e.target.value })}
                        />
                        <label className="form-check-label" htmlFor="cashflow-send-scheduled">
                          Schedule send
                        </label>
                      </div>
                    </div>
                    {formData.send_mode === 'scheduled' && (
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={formData.scheduled_send_local || ''}
                        onChange={(e) => setFormData({ ...formData, scheduled_send_local: e.target.value })}
                        required
                      />
                    )}
                    <div className="form-text">Choose "Schedule send" to send the magic link later.</div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">SharePoint Folder</label>
                    <select
                      className="form-select"
                      value={formData.sharepoint_folder || ''}
                      onChange={(e) => handleSpFolderChange(e.target.value)}
                    >
                      <option value="">— Select SharePoint folder (optional) —</option>
                      <option value="__new__">+ Create new SharePoint folder</option>
                      {[...spClients].sort((a, b) => a.localeCompare(b)).map(name => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                    {formData.sharepoint_folder === '__new__' && (
                      <input
                        type="text"
                        className="form-control mt-2"
                        placeholder='Folder name, e.g. "Schmitz, Greg"'
                        value={formData.newSpFolderName}
                        onChange={(e) => setFormData({ ...formData, newSpFolderName: e.target.value })}
                      />
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Knowledgebase Client</label>
                    <select
                      className="form-select"
                      value={formData.knowledgebase_client_id || ''}
                      onChange={(e) => setFormData({ ...formData, knowledgebase_client_id: e.target.value, newKbClientName: '' })}
                    >
                      <option value="">— Link to knowledgebase client (optional) —</option>
                      <option value="__new__">+ Create new knowledgebase client</option>
                      {[...kbClients].sort((a, b) => a.client_name.localeCompare(b.client_name)).map(c => (
                        <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
                      ))}
                    </select>
                    {formData.knowledgebase_client_id === '__new__' && (
                      <input
                        type="text"
                        className="form-control mt-2"
                        placeholder='Name, e.g. "Schmitz, Greg"'
                        value={formData.newKbClientName}
                        onChange={(e) => setFormData({ ...formData, newKbClientName: e.target.value })}
                      />
                    )}
                    <div className="form-text">Links this client to their knowledgebase record for SharePoint publishing.</div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Client Type</label>
                    <div className="d-flex gap-3">
                      {[
                        { key: 'hff', label: 'HFF' },
                        { key: 'rebellionaire', label: 'Rebellionaire' },
                      ].map((opt) => (
                        <div className="form-check" key={opt.key}>
                          <input
                            className="form-check-input"
                            type="radio"
                            name="client_type"
                            id={`client-type-${opt.key}`}
                            value={opt.key}
                            checked={formData.client_type === opt.key}
                            onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}
                          />
                          <label className="form-check-label" htmlFor={`client-type-${opt.key}`}>
                            {opt.label}
                          </label>
                        </div>
                      ))}
                    </div>
                    <div className="form-text">Determines the visual theme shown to the client on their form.</div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Next Meeting</label>
                    {NEXT_MEETING_OPTIONS.map((opt) => (
                      <div className="form-check" key={opt.key}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="next_meeting"
                          id={`next-meeting-${opt.key}`}
                          value={opt.key}
                          checked={formData.next_meeting === opt.key}
                          onChange={(e) => setFormData({ ...formData, next_meeting: e.target.value })}
                        />
                        <label className="form-check-label" htmlFor={`next-meeting-${opt.key}`}>
                          {opt.label}
                        </label>
                      </div>
                    ))}
                    <div className="form-text">Sets the meeting named in the reminder email. You can change it later from the table.</div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={submitting || (formData.send_mode === 'scheduled' && !formData.scheduled_send_local)}>
                    {submitting ? 'Creating...' : (formData.send_mode === 'scheduled' ? 'Create & Schedule Send' : 'Create & Generate Link')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Get Link Modal */}
      {showLinkModal && linkClient && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Magic Link — {linkClient.name}</h5>
                <button type="button" className="btn-close" onClick={() => setShowLinkModal(false)}></button>
              </div>
              <div className="modal-body">
                {linkLoading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm" role="status"></div>
                    <span className="ms-2">Generating link...</span>
                  </div>
                ) : magicLink ? (
                  <div>
                    <p className="text-muted small mb-2">A new magic link has been generated for this client. The previous link is no longer valid.</p>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        readOnly
                        value={magicLink}
                      />
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => copyToClipboard(magicLink, linkClient.client_id)}
                      >
                        <i className="bi bi-clipboard me-1"></i>
                        {copiedId === linkClient.client_id ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowLinkModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedClient && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Client</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete <strong>{selectedClient.name}</strong> ({selectedClient.email})?</p>
                <p className="text-muted small">This will permanently remove their access and any saved form data.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={handleDelete} disabled={submitting}>
                  {submitting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Publish to SharePoint Modal */}
      {showPublishModal && publishClient && (
        <CashflowSharePointModal
          client={publishClient}
          onClose={() => { setShowPublishModal(false); setPublishClient(null); }}
          onSuccess={handlePublishSuccess}
        />
      )}

      <ClientFormScheduleModal
        isOpen={showScheduleModal}
        title="Schedule Next Cashflow Request"
        client={scheduleClient}
        schedule={scheduleClient ? {
          scheduled_send_at: scheduleClient.next_scheduled_send_at,
          send_reason: scheduleClient.next_scheduled_send_reason,
          send_email: scheduleClient.next_scheduled_send_email,
          reset_progress_on_send: true,
        } : null}
        submitting={submitting}
        onClose={() => { setShowScheduleModal(false); setScheduleClient(null); }}
        onSave={handleScheduleSave}
        onCancelSchedule={handleScheduleCancel}
        reasonOptions={[
          { value: 'next_year', label: 'Next Year Update' },
          { value: 'next_quarter', label: 'Next Quarter Update' },
          { value: 'six_month', label: '6 Month Update' },
          { value: 'custom', label: 'Custom' },
        ]}
        defaultReason="next_year"
      />
    </Wrapper>
  );
};

export default CashflowAdmin;
